import { useState, useEffect, useContext, createContext } from 'react';
import type { ReactNode } from 'react';
import { Session } from '@supabase/supabase-js';
import { authService, type AuthUser } from '../services/auth.service';
import type { Organization } from '../types/database.types';

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  currentOrganization: Organization | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName?: string;
    phone?: string;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  updateProfile: (updates: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    avatar_url?: string;
  }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);

  // Initialize auth state
  useEffect(() => {
    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = authService.onAuthStateChange(async (session) => {
      if (session?.user) {
        const { user: enrichedUser } = await authService.getCurrentSession();
        setUser(enrichedUser);
        setSession(session);
        setCurrentOrganization(enrichedUser?.current_organization || null);
      } else {
        setUser(null);
        setSession(null);
        setCurrentOrganization(null);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const initializeAuth = async () => {
    try {
      const { session, user } = await authService.getCurrentSession();
      setUser(user);
      setSession(session);
      setCurrentOrganization(user?.current_organization || null);
      
      // Test database connection
      await authService.testConnection();
    } catch (error) {
      console.error('Error initializing auth:', error);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const result = await authService.signIn({ email, password });
      setUser(result.user as AuthUser);
      setSession(result.session);
      setCurrentOrganization((result.user as AuthUser)?.current_organization || null);
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName?: string;
    phone?: string;
  }) => {
    setLoading(true);
    try {
      await authService.signUp(userData);
      // Note: User will need to confirm email before they can sign in
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await authService.signOut();
      setUser(null);
      setSession(null);
      setCurrentOrganization(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const switchOrganization = async (organizationId: string) => {
    try {
      const organization = await authService.switchOrganization(organizationId);
      setCurrentOrganization(organization);
    } catch (error) {
      console.error('Switch organization error:', error);
      throw error;
    }
  };

  const updateProfile = async (updates: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    avatar_url?: string;
  }) => {
    try {
      await authService.updateProfile(updates);
      // Refresh user data
      const { user } = await authService.getCurrentSession();
      setUser(user);
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  };

  const value = {
    user,
    session,
    loading,
    currentOrganization,
    signIn,
    signUp,
    signOut,
    switchOrganization,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook for protecting routes
export function useRequireAuth() {
  const { user, loading } = useAuth();
  
  useEffect(() => {
    if (!loading && !user) {
      // Redirect to login page
      window.location.href = '/login';
    }
  }, [user, loading]);

  return { user, loading };
}